// Compute graph representation and execution
//
// Contains low-level functions & types for constructing & executing
// a compute graph.
//
// User (UI) code generally should not need anything other than the Node
// type in this file.

import * as _ from 'lodash';
import * as Types from '../types';

import {ResolverContext} from './resolverContext';

///// Node & Op definitions

// Nodes represent values, Ops represent operations. An op
// always has a single output node of nodeType 'output', and may
// consume many inputs. Many ops may consume the same node.
//
// When constructing a graph by calling the various operations in
// ops.ts, we only store backward references (ops refer to their
// inputs, not the other way around).

// All nodes have a type (using our run-time type system).
export interface BaseNode<T extends Types.Type> {
  type: T;
}

// Ops always produce a single output-node.
export interface OutputNode<T extends Types.Type = Types.Type>
  extends BaseNode<T> {
  nodeType: 'output';
  fromOp: Op;
}

// A variable node just has a name
export interface VarNode<T extends Types.Type = Types.Type>
  extends BaseNode<T> {
  nodeType: 'var';
  varName: string;
}

// A variable node stores its value
export interface ConstNode<T extends Types.Type = Types.Type>
  extends BaseNode<T> {
  nodeType: 'const';
  val: Types.TypeToTSTypeInner<T>;
}

// A void node represents nothing. It's just used to indiciate the lack
// of any node in certain UI circumstances.
export interface VoidNode extends BaseNode<'void'> {
  nodeType: 'void';
}

export type Node<T extends Types.Type = Types.Type> =
  | OutputNode<T>
  | ConstNode<T>
  | VarNode<T>;

export type NodeOrVoidNode<T extends Types.Type = Types.Type> =
  | Node<T>
  | VoidNode;

export const isVoidNode = (node: NodeOrVoidNode): node is VoidNode => {
  return node.nodeType === 'void';
};

export type OpInputs = {[key: string]: Node<any>};

export interface Op {
  name: string;
  inputs: OpInputs;
}

///// "Forward" graph definitions.
//
// Nodes and Ops refer "upstream" ie, they
// refer to their inputs. A forward graph represents the same computation,
// but has downstream references instead of upstream references.

export interface ForwardOp {
  op: Op;
  outputNode: ForwardNode;
}

export interface ForwardNode {
  node: Node;
  hasResult: boolean;
  result: any;
  inputTo: Set<ForwardOp>;
}

export interface ForwardGraph {
  roots: Set<ForwardOp>;
  ops: Map<Op, ForwardOp>;
}

///// Op registration.
//
// The following types are used to represent available
// ops, their resolvers, and other information we need to execute them.

// A fully defined op, that we can call on input nodes and execute.
// Think of it like a function definition.
export interface OpDefinition {
  name: string;

  // argument specification
  inputTypes: InputTypes;

  // return type (may be a function of arguments)
  outputType: Types.Type | TypeFn<'any'>;

  // Function that can actually be called on some nodes,
  // producing an output node. This doesn't execute anything, it
  // just builds the graph.
  op: OpFn;

  // Function that actually executes the op
  resolver: OpResolverFn;

  // Optional function that gets a stronger type, potentially by
  // making a query.
  resolveOutputType?: ResolveTypeFn;
}

// Note, we just use anys here, because these are just stored in the registry
// which is used at runtime. Its not very helpful to have compile-time type
// info here.
type OpFn = (inputs: OpInputTypes<any>) => OutputNode<any>;

export type OpResolverFn = (
  inputs: OpResolverInputTypes<any>,
  forwardGraph: ForwardGraph,
  forwardOp: ForwardOp,
  context: ResolverContext
) => any;

export type TypeFn<T extends Types.Type> = (op: OpInputTypes<any>) => T;
type ResolveTypeFn = (
  op: Op,
  context: ResolverContext
) => Promise<OutputNode<Types.Type>>;

export const registeredOps: {
  [name: string]: OpDefinition;
} = {};

function registerOp(
  name: string,
  inputTypes: InputTypes,
  outputType: Types.Type | TypeFn<'any'>,
  op: OpFn,
  resolver: OpResolverFn,
  resolveOutputType?: ResolveTypeFn
) {
  registeredOps[name] = {
    name,
    inputTypes,
    outputType,
    op,
    resolver,
    resolveOutputType,
  };
}
export type InputTypes = {[key: string]: Types.Type};
export type OpInputTypes<I extends InputTypes> = {
  [K in keyof I]: Node<I[K]>;
};

type OpResolverInputTypes<I extends InputTypes> = {
  [K in keyof I]: Types.TypeToTSTypeInner<I[K]>;
};

// Call this to construct a new op that can be called in our
// code or by the user in the UI.
export function makeOp<
  ON extends string,
  RT extends Types.Type,
  I extends InputTypes
>(
  opName: ON,
  inputTypes: I,
  returnType: RT | TypeFn<RT>,
  resolver?: OpResolverFn,
  resolveOutputType?: ResolveTypeFn
) {
  const opFn = (inputs: OpInputTypes<I>): OutputNode<RT> => {
    const type =
      typeof returnType === 'function' ? returnType(inputs) : returnType;
    return {
      nodeType: 'output',
      type,
      fromOp: {
        name: opName,
        inputs,
      },
    };
  };
  const res =
    resolver ??
    ((inputs: any) => {
      throw new Error('resolver not implemented for ' + opName);
    });
  registerOp(
    opName,
    inputTypes,
    returnType as any,
    opFn,
    res,
    resolveOutputType
  );
  return opFn;
}

export function constNodeIsType<T extends Types.Type>(
  constNode: ConstNode<Types.Type>,
  type: T
): constNode is ConstNode<T> {
  return _.isEqual(constNode.type, type);
}

export function voidNode(): VoidNode {
  return {nodeType: 'void', type: 'void'};
}

export function varNode<T extends Types.Type>(
  type: T,
  varName: string
): VarNode<T> {
  return {
    nodeType: 'var',
    type,
    varName,
  };
}

export function getOpDef(name: string): OpDefinition {
  const opDef = registeredOps[name];
  if (opDef == null) {
    throw new Error('op not registered: ' + name);
  }
  return opDef;
}

export function forwardOpToString(fo: ForwardOp): string {
  let result = fo.op.name;
  if (fo.outputNode.inputTo.size > 0) {
    result += ':';
  }
  for (const ifo of fo.outputNode.inputTo) {
    result += ' ' + ifo.op.name;
  }
  return result;
}

export function forwardOpInputs(
  fg: ForwardGraph,
  fo: ForwardOp
): {[x: string]: any} {
  return _.mapValues(fo.op.inputs, inputNode => {
    if (inputNode.nodeType === 'output') {
      const inputForwardOp = fg.ops.get(inputNode.fromOp)!;
      return inputForwardOp.outputNode.result;
    } else if (inputNode.nodeType === 'const') {
      return inputNode.val;
    } else {
      throw new Error('invalid input in forwardOp');
    }
  });
}

export function findChildOp(
  fo: ForwardOp,
  name: string
): ForwardOp | undefined {
  return Array.from(fo.outputNode.inputTo).find(
    nextOp => nextOp.op.name === name
  );
}

export function newForwardGraph(): ForwardGraph {
  return {roots: new Set(), ops: new Map()};
}

function isRootOp(op: Op) {
  return Object.values(op.inputs).every(node => node.nodeType !== 'output');
}

function updateForwardGraphVisitOp(
  fg: ForwardGraph,
  op: Op,
  outputNode: Node,
  inputToOp?: ForwardOp
) {
  let forwardOp = fg.ops.get(op);
  if (forwardOp != null) {
    if (inputToOp != null) {
      forwardOp.outputNode.inputTo.add(inputToOp);
    }
    return;
  }
  forwardOp = {
    op,
    outputNode: {
      node: outputNode,
      hasResult: false,
      result: undefined,
      inputTo: new Set(),
    },
  };
  if (inputToOp != null) {
    forwardOp.outputNode.inputTo.add(inputToOp);
  }
  if (isRootOp(op)) {
    fg.roots.add(forwardOp);
  }
  fg.ops.set(op, forwardOp);
  for (const inputNode of Object.values(op.inputs)) {
    updateForwardGraphVisitNode(fg, inputNode, forwardOp);
  }
}

function updateForwardGraphVisitNode(
  fg: ForwardGraph,
  node: Node,
  inputToOp?: ForwardOp
) {
  if (node.nodeType === 'output') {
    updateForwardGraphVisitOp(fg, node.fromOp, node, inputToOp);
  }
}

export function updateForwardGraph(fg: ForwardGraph, node: Node) {
  // TODO: do we need to use WeakMap to avoid leaks?
  updateForwardGraphVisitNode(fg, node);
}

async function resolveForwardOp(
  context: ResolverContext,
  fg: ForwardGraph,
  forwardOp: ForwardOp
): Promise<any> {
  const op = forwardOp.op;
  const resolver = registeredOps[op.name].resolver;
  if (resolver == null) {
    throw new Error('no resolver for ' + op.name);
  }
  const opInputs = _.mapValues(op.inputs, inputNode => {
    if (inputNode.nodeType === 'output') {
      const inputForwardOp = fg.ops.get(inputNode.fromOp)!;
      return inputForwardOp.outputNode.result;
    } else if (inputNode.nodeType === 'const') {
      return inputNode.val;
    } else {
      console.log('INVALID INPUT IN FORWARD OP', inputNode);
      throw new Error('invalid input in forwardOp');
    }
  });
  // You can do something like this to put spans in the chrome profiler for
  // each op (not quite right yet).
  // performance.mark('op-start-' + op.name);
  // return Promise.resolve(resolver(opInputs, fg, forwardOp, context)).then(r => {
  //   performance.mark('op-end-' + op.name);
  //   performance.measure(
  //     'op-' + op.name,
  //     'op-start-' + op.name,
  //     'op-end-' + op.name
  //   );
  //   return r;
  // });
  return Promise.resolve(resolver(opInputs, fg, forwardOp, context));
}

export async function executeForward(
  context: ResolverContext,
  fg: ForwardGraph
): Promise<void> {
  // Breadth first search.
  // Only enqueue nodes whose inputs have been resolved.
  let toExecuteOps = Array.from(fg.roots);
  while (toExecuteOps.length > 0) {
    const executeNowOps = toExecuteOps;
    toExecuteOps = [];

    // Compute set of ops that need a result (those that don't
    // already have a result)
    const needResultIndexes: number[] = [];
    for (let i = 0; i < executeNowOps.length; i++) {
      const forwardOp = executeNowOps[i];
      if (!forwardOp.outputNode.hasResult) {
        needResultIndexes.push(i);
      }
    }

    // Execute in parallel
    const proms = needResultIndexes.map(i =>
      resolveForwardOp(context, fg, executeNowOps[i])
    );
    const results = await Promise.all(proms);

    // Update nodes with results
    for (let i = 0; i < results.length; i++) {
      const forwardOp = executeNowOps[needResultIndexes[i]];
      const result = results[i];
      if (!forwardOp.outputNode.hasResult) {
        forwardOp.outputNode.hasResult = true;
        forwardOp.outputNode.result = result;
      }
    }

    // Schedule any nodes that now have all of their inputs ready
    for (const forwardOp of executeNowOps) {
      for (const nextForwardOp of forwardOp.outputNode.inputTo) {
        let inputsFilled = true;
        for (const nextForwardOpInputNode of Object.values(
          nextForwardOp.op.inputs
        )) {
          if (nextForwardOpInputNode.nodeType === 'output') {
            const producingForwardOp = fg.ops.get(
              nextForwardOpInputNode.fromOp
            );
            if (producingForwardOp == null) {
              throw new Error('Invalid forward op');
            }
            if (!producingForwardOp.outputNode.hasResult) {
              inputsFilled = false;
              break;
            }
          }
        }
        if (inputsFilled) {
          toExecuteOps.push(nextForwardOp);
        }
      }
    }
  }
}

export async function getNodeResults(
  fg: ForwardGraph,
  targetNodes: NodeOrVoidNode[]
): Promise<any[]> {
  return targetNodes.map(node => {
    if (node.nodeType === 'void') {
      return undefined;
    } else if (node.nodeType === 'output') {
      return fg.ops.get(node.fromOp)!.outputNode.result;
    } else if (node.nodeType === 'const') {
      return node.val;
    }
    throw new Error('invalid targetNode');
  });
}

export async function executeForwardFresh(
  context: ResolverContext,
  targetNodes: NodeOrVoidNode[]
): Promise<any[]> {
  const fg = newForwardGraph();
  for (const node of targetNodes) {
    if (node.nodeType !== 'void') {
      updateForwardGraph(fg, node);
    }
  }

  await executeForward(context, fg);

  return getNodeResults(fg, targetNodes);
}

// TODO: Typescript does't want to return the TypeToTSTypeInner type.
//     it says "type instantiation is excessively deep". Its odd because
//     we also need TypeToTSTypeInner to resolve node, but that works
// Should fix this, its only an issue with the const string test for some
//     reason. It works elsewhere and exploses other bugs
export async function execute<T extends Types.Type>(
  context: ResolverContext,
  node: Node<T>
) {
  const result = await executeForwardFresh(context, [node]);
  return result[0];
}
